import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Outlet, useMatches, useNavigate, redirect } from "@tanstack/react-router";
import { deleteQueueGroupFn, queueGroupsQueryOptions } from "@/server/queues";
import { DashboardLayout } from "@/components/dashboard-layout";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { toast } from "sonner";
import { CrudEntry } from "@/lib/mma/types.gen";
import { ContextUser, checkIfUserMayWrite, checkIfUserMayDelete, getPermissionRestraintMessage } from "@/lib/utils";
import { usePermissionRedirect } from "@/hooks/use-permission-redirect";

export const Route = createFileRoute("/_dashboard/queue-groups")({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    const user = context.user as ContextUser;
  },
  loader: async ({ context }) => {
    return {
      user: context.user as ContextUser | null | undefined,
      crumb: "Queue Groups",
      userProfileId: (context as { user?: { profile: string } }).user?.profile,
      accessControl: (context as { user?: { accessControl: Record<string, CrudEntry> } }).user?.accessControl,
    };
  },
});

function RouteComponent() {
  const { userProfileId, accessControl, user } = Route.useLoaderData();
  usePermissionRedirect({ user, permission: "QUEUE_GROUP" });
  //If the user has no profile, it means they have access to everything. Profiles are restrictive only.
  const mayWrite = checkIfUserMayWrite(userProfileId, accessControl, "QUEUE_GROUP");
  const mayDelete = checkIfUserMayDelete(userProfileId, accessControl, "QUEUE_GROUP");
  const navigate = useNavigate();
  const queueGroupsQuery = useQuery(queueGroupsQueryOptions);
  const queueGroups = queueGroupsQuery.data ?? [];
  const matches = useMatches();
  const isRootQueueGroups = matches[matches.length - 1].fullPath === "/queue-groups";
  const queryClient = useQueryClient();
  const [selectedQueueGroup, setSelectedQueueGroup] = useState<{ id: number; name: string } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  function handleDeleteClick(queueGroupId: number, queueGroupName: string) {
    setSelectedQueueGroup({ id: queueGroupId, name: queueGroupName });
    setDialogOpen(true);
  }

  const deleteQueueGroupMutation = useMutation({
    mutationFn: deleteQueueGroupFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["queueGroups"] });
      queueGroupsQuery.refetch();
      setDialogOpen(false);
      setSelectedQueueGroup(null);
      setLoading(false);
      navigate({ to: "/queue-groups" });
    },
  });

  async function handleConfirmDelete() {
    if (!selectedQueueGroup) return;
    setLoading(true);
    const deleteQueueGroupPromise = deleteQueueGroupMutation.mutateAsync({
      data: { queueGroupId: Number(selectedQueueGroup.id) },
    });
    toast.promise(deleteQueueGroupPromise, {
      loading: "Deleting queue group...",
      success: () => {
        return "Queue group deleted successfully";
      },
      error: "Failed to delete queue group",
    });
  }

  return (
    <>
      <DashboardLayout
        submenu={{
          items: queueGroups,
          isLoading: queueGroupsQuery.isLoading,
          onClick: (item: any, functionType: string, parent?: { name: string; id: string }) => {
            if (functionType === "delete") {
              if (!mayDelete) {
                toast.error(getPermissionRestraintMessage("delete queue groups"));
                return;
              }
              handleDeleteClick(item.parent.id, item.parent.name);
            }
          },
          title: "Queue Groups",
          active: true,
          createNew: mayWrite ? [{ label: "+ Create new queue group", href: "/queue-groups/new-group" }] : undefined,
        }}
      >
        {isRootQueueGroups ? (
          <Card>
            <CardContent>
              <p className="text-md pb-20 pt-15 text-center">
                Please select a queue group from the submenu to view its details or manage it.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Outlet />
        )}
      </DashboardLayout>
      <ConfirmDialog
        open={dialogOpen}
        title="Delete queue group"
        description={
          selectedQueueGroup ? `Are you sure you want to delete the queue group "${selectedQueueGroup.name}"?` : ""
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDialogOpen(false);
          setSelectedQueueGroup(null);
        }}
        loading={loading}
      />
    </>
  );
}
